import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sun, MapPin, Clock, ShieldAlert, CloudRain, Wind, Truck, ChevronRight,
  CheckCircle2, AlertOctagon, Package, FileSignature, Navigation2,
} from "lucide-react";
import api from "../lib/api";

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" }); } catch { return ""; }
}

function fmtDateLong(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" });
}

function fullAddr(addr) {
  if (!addr) return "";
  return [addr.line1, addr.suburb, addr.state, addr.postcode].filter(Boolean).join(", ");
}

function mapsLink(addr) {
  if (!addr) return null;
  if (addr.lat && addr.lng) return `https://www.google.com/maps/dir/?api=1&destination=${addr.lat},${addr.lng}`;
  const a = fullAddr(addr);
  return a ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a)}` : null;
}

export default function MyDay() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/field/my-day");
      setData(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const blocks = data?.blocks || [];
  const weather = data?.weather;
  const activeVisit = data?.active_visit;

  const blockedCount = useMemo(
    () => blocks.filter((b) => !b.materials_received).length,
    [blocks]
  );

  return (
    <div data-testid="my-day-page" className="px-4 pt-4 pb-6 max-w-[640px] mx-auto">
      {/* Date header */}
      <div className="flex items-center gap-2 mb-1">
        <Sun size={14} style={{ color: "var(--accent)" }} />
        <span className="text-[10.5px] font-extrabold uppercase tracking-[0.18em]"
              style={{ color: "var(--ink-muted)" }}>
          My Day
        </span>
      </div>
      <h1 className="text-[22px] leading-tight font-extrabold" style={{ color: "var(--ink)" }} data-testid="my-day-date">
        {fmtDateLong(data?.date) || "Today"}
      </h1>

      {/* Weather strip */}
      {weather && (
        <div
          className="mt-3 flex items-center gap-2 px-3 py-2 text-[12px]"
          data-testid="my-day-weather"
          style={{
            background: "var(--bg-elev-1)",
            border: "1px solid var(--line)",
            borderLeft: `3px solid ${weather.severe ? "var(--bad)" :
              (weather.rain_risk === "high" || weather.wind_risk === "high") ? "var(--warn)" : "var(--brand)"}`,
            borderRadius: 4,
          }}
        >
          <CloudRain size={13} style={{ color: "var(--ink-muted)" }} />
          <span className="capitalize font-bold" style={{ color: "var(--ink)" }}>{weather.rain_risk || "none"}</span>
          <span className="opacity-30">·</span>
          <Wind size={13} style={{ color: "var(--ink-muted)" }} />
          <span className="capitalize font-bold" style={{ color: "var(--ink)" }}>{weather.wind_risk || "none"}</span>
          {weather.summary && (
            <>
              <span className="opacity-30">·</span>
              <span style={{ color: "var(--ink-soft)" }} className="truncate">{weather.summary}</span>
            </>
          )}
        </div>
      )}

      {/* Active visit banner */}
      {activeVisit && (
        <div
          data-testid="active-visit-banner"
          className="mt-4 px-3 py-3 flex items-center gap-3"
          style={{
            background: "var(--bg-nav)",
            color: "#fff",
            borderRadius: 6,
            borderLeft: "4px solid var(--accent)",
          }}
        >
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "rgba(255,255,255,0.08)" }}>
            <MapPin size={16} style={{ color: "var(--accent)" }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[9.5px] font-extrabold uppercase tracking-[0.18em] text-white/55">Onsite now</div>
            <div className="text-[13px] font-bold truncate">
              Started {fmtTime(activeVisit.started_at)}
            </div>
          </div>
          <Link
            to={`/visit/${activeVisit.id}`}
            className="text-[10.5px] font-extrabold uppercase tracking-[0.14em] px-3 py-2 rounded-md"
            style={{ background: "var(--accent)", color: "#fff" }}
            data-testid="continue-visit-btn"
          >
            Continue →
          </Link>
        </div>
      )}

      {/* Snapshot strip */}
      <div className="grid grid-cols-3 gap-2 mt-4" data-testid="my-day-snapshot">
        <Snap label="Jobs" value={blocks.length} accent="var(--brand)" testId="snap-jobs" />
        <Snap label="Need mats" value={blockedCount} accent={blockedCount > 0 ? "var(--warn)" : "var(--ink-muted)"} testId="snap-need-mats" />
        <Snap label="Signed off" value={blocks.filter((b) => b.signed_off).length} accent="var(--good)" testId="snap-signed-off" />
      </div>

      {/* Jobs list */}
      <div className="mt-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10.5px] font-extrabold uppercase tracking-[0.18em]"
                style={{ color: "var(--ink-muted)" }}>
            Today's jobs
          </span>
          <span className="text-[10.5px] font-mono" style={{ color: "var(--ink-faint)" }}>
            {blocks.length}
          </span>
        </div>

        {loading && (
          <div className="text-[12px]" style={{ color: "var(--ink-muted)" }} data-testid="my-day-loading">Loading…</div>
        )}

        {!loading && blocks.length === 0 && (
          <div
            data-testid="my-day-empty"
            className="px-4 py-8 text-center"
            style={{ background: "var(--bg-elev-1)", border: "1px dashed var(--line-strong)", borderRadius: 6 }}
          >
            <ShieldAlert size={20} style={{ color: "var(--ink-faint)", margin: "0 auto" }} />
            <div className="mt-2 text-[13px] font-bold" style={{ color: "var(--ink)" }}>No jobs assigned today</div>
            <div className="mt-1 text-[11px]" style={{ color: "var(--ink-muted)" }}>
              Check back tomorrow, or contact your supervisor.
            </div>
          </div>
        )}

        <div className="space-y-2.5">
          {blocks.map((b) => (
            <JobRow key={b.id} block={b} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Snap({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="px-3 py-2.5 flex items-center justify-between"
      style={{
        background: "var(--bg-elev-1)",
        border: "1px solid var(--line)",
        borderLeft: `3px solid ${accent}`,
        borderRadius: 4,
      }}
    >
      <span className="text-[9.5px] font-extrabold uppercase tracking-[0.14em]"
            style={{ color: "var(--ink-muted)" }}>{label}</span>
      <span className="text-[20px] font-extrabold font-mono leading-none" style={{ color: "var(--ink)" }}>
        {value}
      </span>
    </div>
  );
}

function JobRow({ block }) {
  const nav = useNavigate();
  const addr = block.project_address;
  const mapHref = mapsLink(addr);
  const isComplete = block.signed_off;
  const accent = isComplete ? "var(--good)" : (!block.materials_received ? "var(--warn)" : "var(--brand)");
  const go = () => nav(`/jobs/${block.id}`);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
      data-testid={`my-day-job-${block.id}`}
      className="block hover:bg-[var(--bg-elev-2)] cursor-pointer"
      style={{
        background: "var(--bg-elev-1)",
        border: "1px solid var(--line)",
        borderLeft: `4px solid ${accent}`,
        borderRadius: 4,
      }}
    >
      <div className="px-3 pt-3 pb-2 flex items-center gap-3">
        <div className="font-mono leading-none">
          <div className="text-[15px] font-extrabold" style={{ color: "var(--ink)" }}>{fmtTime(block.start)}</div>
          <div className="text-[10px] mt-0.5 font-bold" style={{ color: "var(--ink-muted)" }}>
            → {fmtTime(block.end)}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[11.5px]">
            <span className="font-mono font-extrabold" style={{ color: "var(--brand)" }}>{block.project_code}</span>
            {isComplete && <span className="chip chip-good" data-testid="job-complete-chip">DONE</span>}
          </div>
          <div className="text-[13.5px] font-bold truncate mt-0.5" style={{ color: "var(--ink)" }}>
            {block.project_name}
          </div>
          {addr && (
            <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--ink-muted)" }}>
              <MapPin size={10} className="inline -mt-[2px] mr-1" />{fullAddr(addr) || "No address"}
            </div>
          )}
        </div>
        <ChevronRight size={16} style={{ color: "var(--ink-muted)" }} />
      </div>

      {/* Readiness chips */}
      <div
        className="px-3 py-2 flex items-center gap-1.5 flex-wrap text-[10.5px]"
        style={{ borderTop: "1px solid var(--line)", background: "var(--bg-elev-2)" }}
      >
        <Chip
          icon={Package}
          label={block.materials_received ? "Materials ✓" : "Materials pending"}
          tone={block.materials_received ? "good" : "warn"}
          testId={`chip-materials-${block.id}`}
        />
        {block.variations_pending > 0 && (
          <Chip icon={AlertOctagon} label={`${block.variations_pending} variation`} tone="warn"
                testId={`chip-variations-${block.id}`} />
        )}
        {block.signed_off ? (
          <Chip icon={CheckCircle2} label="Signed off" tone="good" testId={`chip-signed-${block.id}`} />
        ) : (
          <Chip icon={FileSignature} label="Awaiting sign-off" tone="muted" testId={`chip-pending-${block.id}`} />
        )}
        {mapHref && (
          <a
            href={mapHref}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-bold uppercase tracking-[0.10em]"
            style={{ background: "var(--bg-nav)", color: "#fff" }}
            data-testid={`directions-btn-${block.id}`}
          >
            <Navigation2 size={11} /> Directions
          </a>
        )}
      </div>
    </div>
  );
}

function Chip({ icon: Icon, label, tone = "muted", testId }) {
  const toneMap = {
    good: { bg: "rgba(27,154,92,0.10)", fg: "var(--good)", border: "rgba(27,154,92,0.30)" },
    warn: { bg: "rgba(216,144,52,0.12)", fg: "var(--warn)", border: "rgba(216,144,52,0.30)" },
    bad: { bg: "rgba(230,20,50,0.10)", fg: "var(--bad)", border: "rgba(230,20,50,0.30)" },
    muted: { bg: "rgba(108,110,134,0.10)", fg: "var(--ink-muted)", border: "var(--line)" },
  }[tone];
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold uppercase tracking-[0.08em]"
      style={{ background: toneMap.bg, color: toneMap.fg, border: `1px solid ${toneMap.border}` }}
    >
      <Icon size={11} />
      {label}
    </span>
  );
}
